import {
  BookMarked,
  FolderTree,
  House,
  Orbit,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings,
  Waypoints,
} from "lucide-react";
import { useState, type ComponentType, type ReactNode } from "react";

import { workspace } from "@/components/workspace/controller";
import { ToolsButton } from "@/components/appshell/ToolsOverlay";
import { useWorkspaceStore } from "@/components/workspace/store";
import { ExplorerPane } from "@/components/files/ExplorerPane";
import { TopBarToggle } from "@/components/appshell/TopBarToggle";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger, Tip } from "@/components/ui/overlays";
import { QuickAddItems } from "@/components/shared/QuickAddMenu";
import { ATLAS_MASTER_LABEL, openAtlasMasterFile } from "@/lib/create-note";
import { cn } from "@/lib/utils";
import { useShell } from "@/stores/shell";

function RibbonButton({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Tip label={label} side="right">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onClick}
        aria-label={label}
        aria-current={active}
        className={cn("h-8 w-8", active && "bg-primary/15 text-primary hover:text-primary")}
      >
        <Icon className="h-4 w-4" />
      </Button>
    </Tip>
  );
}

/** The ribbon's "+" — the same quick-add list the atlas offers on a two-click,
 *  but reachable from the rail on every platform, collapsed or not.
 *
 *  It hangs off a Popover rather than the atlas's absolutely-positioned popup on
 *  purpose: the rail is flush against the left edge and often near the bottom of
 *  a short window, where a fixed-offset panel runs off the screen. The popover's
 *  positioner flips and shifts the panel to keep it in the viewport, and caps its
 *  height so a small screen scrolls the list instead of clipping it. */
function QuickAddRibbonButton({ onAddWebsite }: { onAddWebsite: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tip label="New note, file, or website" side="right">
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="New note, file, or website"
            className="h-8 w-8"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent align="start" className="w-[190px] p-1">
        <p className="px-2 pb-1 pt-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
          Add to the atlas
        </p>
        <div className="max-h-[60vh] overflow-y-auto">
          <QuickAddItems onClose={() => setOpen(false)} onAddWebsite={onAddWebsite} />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SectionHeader({
  label,
  open,
  onToggle,
  actions,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  /** Trailing controls rendered on the same row as the label (siblings of the
   * toggle button, never nested inside it). */
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-center pr-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex flex-1 items-center px-2 py-1 text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
      >
        {label}
      </button>
      {actions}
    </div>
  );
}

function FilesSection({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ONE Files header. The per-folder "new subfolder" affordance lives on
          each folder row (and in its context menu), so the header carries the
          escape hatch instead: this rail is 252px, and Folder View is where the
          work that needs room happens. */}
      <SectionHeader
        label="Files"
        open={open}
        onToggle={onToggle}
        actions={
          open ? (
            <Tip label="Open Folder View" side="right">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                aria-label="Open Folder View"
                onClick={() => workspace.openFiles()}
              >
                <FolderTree className="h-3.5 w-3.5" />
              </Button>
            </Tip>
          ) : undefined
        }
      />
      {open && (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <ExplorerPane embedded />
        </div>
      )}
    </div>
  );
}

/**
 * The left rail: ONE job, the library.
 *
 * A top ribbon (Home, Atlas, Orbit), a Tools button that opens the overlay, the
 * file tree, and a bottom Settings button. Collapses to a slim icon spine; the
 * ribbon and Settings stay reachable even collapsed.
 *
 * It used to hold a second collapsible list of Tools, swappable above or below
 * Files. That broke containment: a rail that answers "what do I have?" was also
 * answering "what can I do?", and it was the reason nobody could find an action
 * twice. The verbs live in the Tools overlay now.
 */
export function LeftBar({ onAddWebsite }: { onAddWebsite: () => void }) {
  const open = useShell((s) => s.leftBarOpen);
  // The ribbon reads/drives the workspace controller directly (its singleton
  // graph:main/orbit:main panels), not the `stores/tabs` shim — "active"
  // means "the workspace's active panel is Atlas/Orbit", not "the whole app
  // is showing nothing else" (many panels can be open at once now).
  const activePanelType = useWorkspaceStore((s) => s.panels[s.activeId ?? ""]?.type);
  const [filesOpen, setFilesOpen] = useState(true);

  const ribbonButtons = (
    <>
      {/* Home leads the ribbon: it is the landing destination, so it takes the
          slot the eye reaches first — top of the rail when expanded, top of the
          spine when collapsed. */}
      <RibbonButton
        icon={House}
        label="Home"
        active={activePanelType === "home"}
        onClick={() => {
          workspace.openHome();
          useShell.getState().openTopBar();
        }}
      />
      {/* Opening a destination also reveals the top bar, because that is where
          search lives: arriving at the atlas almost always means looking for
          something in it, and needing a second click on a different edge to
          get a search box made the two feel unrelated. */}
      <RibbonButton
        icon={Waypoints}
        label="Atlas"
        active={activePanelType === "graph"}
        onClick={() => {
          workspace.openGraph();
          useShell.getState().openTopBar();
        }}
      />
      <RibbonButton
        icon={Orbit}
        label="Orbit"
        active={activePanelType === "orbit"}
        onClick={() => {
          workspace.openOrbit();
          useShell.getState().openTopBar();
        }}
      />
      {/* The vault's fixed landing page. It sits with Atlas and Orbit because it
          is the same kind of thing — one always-there destination, not a file
          you happen to have made. */}
      <RibbonButton
        icon={BookMarked}
        label={ATLAS_MASTER_LABEL}
        active={false}
        onClick={() => void openAtlasMasterFile()}
      />
      <QuickAddRibbonButton onAddWebsite={onAddWebsite} />
    </>
  );

  if (!open) {
    return (
      <nav
        id="gv-library"
        aria-label="Library"
        className="flex w-11 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
      >
        {/* Destinations first, then the chrome toggles that bring the shell
            back — both in the top-left where they are reached for, rather than
            parked at the foot of a full-height column. Settings stays at the
            bottom, where a settings button belongs. */}
        <div className="flex flex-col items-center gap-1 py-2">{ribbonButtons}</div>
        <div className="my-1 h-px bg-sidebar-border/60" />
        <div className="flex flex-col items-center gap-1 pb-2">
          <Tip label="Expand sidebar — [" side="right">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => useShell.getState().toggleLeftBar()}
              aria-label="Expand sidebar"
              aria-expanded={false}
              aria-controls="gv-library"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </Button>
          </Tip>
          <TopBarToggle side="right" />
        </div>
        <div className="flex flex-1 flex-col items-center justify-end gap-1 py-2">
          <Tip label="Settings" side="right">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => useShell.getState().openSettings()}
              aria-label="Settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </Tip>
        </div>
      </nav>
    );
  }

  const filesSection = <FilesSection open={filesOpen} onToggle={() => setFilesOpen((v) => !v)} />;

  return (
    <nav
      id="gv-library"
      aria-label="Library"
      className="flex w-[252px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
    >
      <div className="flex items-center border-b border-sidebar-border/60 px-1.5">
        <div className="flex items-center gap-1 py-1.5">{ribbonButtons}</div>
        <div className="ml-auto flex items-center gap-0.5">
          <TopBarToggle side="right" />
          <Tip label="Collapse sidebar — [" side="right">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => useShell.getState().toggleLeftBar()}
              aria-label="Collapse sidebar"
              aria-expanded
              aria-controls="gv-library"
            >
              <PanelLeftClose className="h-4 w-4" />
            </Button>
          </Tip>
        </div>
      </div>

      {/* One job for this rail: the library. Tools used to live here as a
          second collapsible list, which broke containment — a rail that answers
          "what do I have?" was also answering "what can I do?". The verbs now
          live in the Tools overlay, and this button is the door to it. */}
      <div className="shrink-0 border-b border-sidebar-border/60 p-1.5">
        <ToolsButton />
      </div>

      <div className="flex min-h-0 flex-1 flex-col">{filesSection}</div>

      <div className="border-t border-sidebar-border/60 p-1.5">
        <Tip label="Settings" side="right">
          <button
            type="button"
            onClick={() => useShell.getState().openSettings()}
            className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] text-sidebar-foreground/85 transition-colors hover:bg-accent hover:text-foreground"
          >
            <Settings className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            Settings
          </button>
        </Tip>
      </div>
    </nav>
  );
}
